/**
 * "Unsaved changes" guard shared by the editor and the shell. The top bar navigates from outside the
 * editor, so both consult this one flag before leaving a screen with edits that haven't been saved.
 */
export type SaveStatus = "saved" | "dirty" | "saving" | "error";

let unsaved = false;
let saveStatus: SaveStatus = "saved";
let saveError: string | null = null;
const statusListeners = new Set<(s: SaveStatus, err?: string | null) => void>();

export const setUnsaved = (d: boolean) => {
  unsaved = d;
  if (!d && saveStatus === "dirty") {
    setSaveStatus("saved");
  } else if (d && saveStatus === "saved") {
    setSaveStatus("dirty");
  }
};
export const isUnsaved = () => unsaved;

export const setSaveStatus = (s: SaveStatus, err: string | null = null) => {
  saveStatus = s;
  saveError = err;
  if (s === "dirty") unsaved = true;
  else if (s === "saved") unsaved = false;
  statusListeners.forEach((l) => l(s, err));
};

export const getSaveStatus = () => saveStatus;
export const getSaveError = () => saveError;
export const subscribeSaveStatus = (l: (s: SaveStatus, err?: string | null) => void) => {
  statusListeners.add(l);
  return () => { statusListeners.delete(l); };
};

/**
 * A change that will save itself in a moment (a callout edit, inspector edit, or canvas edit).
 */
let pending = false;
type PendingListener = (p: boolean) => void;
const pendingListeners = new Set<PendingListener>();

export const setPendingSave = (p: boolean) => {
  pending = p;
  if (p && saveStatus === "saved") {
    setSaveStatus("dirty");
  }
  pendingListeners.forEach((l) => l(p));
};
export const hasPendingSave = () => pending;
export const subscribePendingSave = (l: PendingListener) => {
  pendingListeners.add(l);
  return () => { pendingListeners.delete(l); };
};

/** True if it's fine to navigate away: nothing unsaved, or the person agreed to discard it. */
export function confirmLeave(): boolean {
  if (!unsaved && !pending && saveStatus !== "dirty" && saveStatus !== "saving") return true;
  if (!confirm("You have unsaved changes on this step. Leave without saving?")) return false;
  unsaved = false;
  pending = false;
  setSaveStatus("saved");
  return true;
}
